/** Subscribe through the command queue so mounting before the widget boots is safe. */
export const subscribeHelplyUnread = listener => {
  if (typeof window === 'undefined' || typeof window.Helply !== 'function') {
    return () => {}
  }
  let active = true
  const onUnread = unread => {
    if (active) listener(unread === true)
  }
  window.Helply('on', 'unreadChange', onUnread)
  return () => {
    active = false
    // The real command replaces the loading stub, so resolve it again on cleanup.
    if (typeof window.Helply === 'function') {
      window.Helply('off', 'unreadChange', onUnread)
    }
  }
}

/**
 * Identifies the agent whenever the widget opens, from the widget's own `open` event: the Help button opens
 * it, and so does the widget itself when a Helply reminder email's link brings the agent back to a
 * conversation, with no click here. The identify needs the current user, which `setIdentify` hands over once
 * it is known; an open that comes first is identified then. `listen` registers through the command queue, so
 * at module load it is in place before the widget boots and opens. The widget keeps its listeners in a set,
 * so registering again is a no-op, except after it reloaded its bundle, which drops them; the Help button
 * registers again before it opens.
 */
export const createHelplyOpenIdentify = () => {
  let identify = null
  let openedFirst = false
  const onOpen = () => {
    if (identify) {
      identify()
    } else {
      openedFirst = true
    }
  }
  return {
    listen: () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.Helply === 'function'
      ) {
        window.Helply('on', 'open', onOpen)
      }
    },
    setIdentify: next => {
      identify = next
      if (next && openedFirst) {
        openedFirst = false
        next()
      }
    },
  }
}

const helplyOpenIdentify = createHelplyOpenIdentify()
helplyOpenIdentify.listen()

export const listenForHelplyOpen = helplyOpenIdentify.listen
export const setHelplyIdentify = helplyOpenIdentify.setIdentify

// Fetched fresh on every call, never from the store: Groove signs the token for two hours, and the widget
// calls the provider when it sends an identify and again whenever the token runs out — a token read once
// at page load would be refused on every help click past the second hour, and the agent would chat
// anonymously with nothing said.
//
// Asked of the GraphQL gateway (`user`, backed by Groove's /v1/me), not the v2 API: the v2 API answers
// every query but bootstrap and billing with a 401 for an inactive account, and an agent locked out for
// billing is exactly who opens support chat from the billing page. /v1/me still serves them — it is how
// the bootstrap carried this token before the provider existed.
const helplyIdentifyTokenQuery = `
  query HelplyWidgetIdentifyToken {
    user {
      id
      helplyWidgetIdentifyToken
    }
  }
`

// The widget awaits the provider before the identify goes out — and, when it refreshes, before the request
// that needed the token — so a hung fetch here would hang those. Bounded like the Copilot grant fetch.
export const IDENTIFY_TOKEN_FETCH_TIMEOUT_MS = 15000

/**
 * The token provider `Helply('identify', { token })` takes: resolves the current agent's signed identify
 * token, or null when the environment has no widget secret (then the identify is anonymous, as before).
 * Rejects on a failed or timed-out request, and on a reply that carries no user at all (the client resolves
 * some 401s instead of rejecting them) — the widget warns and leaves the visitor's identity as it was, which
 * is what a failure should do; a null here would instead replace a verified session with an unverified one.
 * `graphql` is injected for the tests; the app passes `api/graphql` (the gateway).
 */
export const createHelplyIdentifyTokenProvider = (
  graphql,
  { timeoutMs = IDENTIFY_TOKEN_FETCH_TIMEOUT_MS } = {}
) => async () => {
  let timeoutId
  const onTimeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Helply identify token fetch timed out')),
      timeoutMs
    )
  })
  try {
    // The client reads the auth token itself; its first argument is ignored.
    const response = await Promise.race([
      graphql(null, helplyIdentifyTokenQuery),
      onTimeout,
    ])
    const user =
      response && response.json && response.json.data && response.json.data.user
    if (!user) {
      throw new Error('Helply identify token fetch returned no user')
    }
    return user.helplyWidgetIdentifyToken || null
  } finally {
    clearTimeout(timeoutId)
  }
}
